import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { ReactNode } from "react";

import { useProjectContentSearchStore } from "~/projectContentSearchStore";
import type { Project, SidebarThreadSummary, Thread } from "~/types";

import { resolveDiffCheckoutPath } from "../diffTargetResolution";
import type { CommandPaletteActionItem } from "../CommandPalette.logic";
import { mergeLiveAndReplicatedSidebarThreads } from "../sidebar/mergeSidebarThreadHistory";

type ProjectContentSearchThread = Pick<
  SidebarThreadSummary,
  "id" | "environmentId" | "projectId" | "worktreePath"
>;

interface ProjectContentSearchActionTarget {
  readonly project: Project;
  readonly thread: ProjectContentSearchThread;
}

export function resolveThreadProject(
  projects: ReadonlyArray<Project>,
  thread: ProjectContentSearchThread | null | undefined,
): Project | null {
  if (!thread) return null;
  return (
    projects.find(
      (project) =>
        project.environmentId === thread.environmentId && project.id === thread.projectId,
    ) ?? null
  );
}

/**
 * Finds the active route in the same merged thread catalog the sidebar uses.
 * Live state remains authoritative, while the CMMD inventory keeps an offline
 * route actionable enough to open this read-only search surface.
 */
export function resolveProjectContentSearchTarget(input: {
  readonly activeThread: Thread | null | undefined;
  readonly liveProjects: ReadonlyArray<Project>;
  readonly liveThreads: ReadonlyArray<SidebarThreadSummary>;
  readonly replicatedProjects: ReadonlyArray<Project>;
  readonly replicatedThreads: ReadonlyArray<SidebarThreadSummary>;
  readonly routeThreadRefs: ReadonlyArray<ScopedThreadRef>;
  readonly savedEnvironments: Readonly<Record<string, { readonly runtimeId?: string }>>;
}): ProjectContentSearchActionTarget | null {
  const threads = mergeLiveAndReplicatedSidebarThreads(
    input.liveThreads,
    input.replicatedThreads,
    input.savedEnvironments,
  );
  const routeThread = input.routeThreadRefs
    .map((ref) =>
      threads.find(
        (thread) => thread.environmentId === ref.environmentId && thread.id === ref.threadId,
      ),
    )
    .find((thread) => thread !== undefined);
  const thread = routeThread ?? input.activeThread;
  if (!thread) return null;
  const project =
    resolveThreadProject(input.liveProjects, thread) ??
    resolveThreadProject(input.replicatedProjects, thread);
  return project ? { project, thread } : null;
}

export function openProjectContentSearch(input: {
  readonly project: Project;
  readonly thread: ProjectContentSearchThread;
}): void {
  const cwd = resolveDiffCheckoutPath({
    threadWorktreePath: input.thread.worktreePath,
    projectCwd: input.project.cwd,
  });
  if (!cwd) return;
  useProjectContentSearchStore.getState().open({
    environmentId: input.thread.environmentId,
    cwd,
    projectName: input.project.name,
    threadRef: scopeThreadRef(input.thread.environmentId, input.thread.id),
  });
}

export function buildProjectContentSearchAction(input: {
  readonly icon: ReactNode;
  readonly target: ProjectContentSearchActionTarget | null;
}): CommandPaletteActionItem | null {
  const target = input.target;
  if (!target) return null;
  return {
    kind: "action",
    value: "action:search-project-contents",
    searchTerms: ["search files", "find in files", "project contents"],
    title: "Search project files",
    icon: input.icon,
    run: async () => openProjectContentSearch(target),
  };
}
